import { Link } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { Skeleton } from '@/components/ui/States';
import { summariseAccess } from '@/features/billing/access-label';
import { useAccess } from '@/features/billing/use-access';
import { authClient } from '@/lib/auth-client';
import { cn } from '@/lib/cn';

/** Sidebar strip: what the student has right now and where to change it. */
export function PlanStatusCard({ className }: { className?: string }) {
  const { data: session } = authClient.useSession();
  const access = useAccess(Boolean(session));

  return (
    <div className={cn('rounded-card bg-ink-panel px-3 py-2 text-ink-panel-text', className)}>
      {access.isPending ? (
        <Skeleton className="h-7 w-full" />
      ) : access.isError ? (
        <p className="text-[13px] leading-snug text-ink-panel-muted">
          We could not load your plan.
        </p>
      ) : (
        <PlanStatusLink data={access.data} />
      )}
    </div>
  );
}

function PlanStatusLink({ data }: { data: NonNullable<ReturnType<typeof useAccess>['data']> }) {
  const summary = summariseAccess(data);
  const to = summary.tone === 'active' ? '/account/billing' : '/pricing';
  return (
    <Link
      to={to}
      className="focus-ring flex items-start gap-1.5 text-[13px] leading-snug no-underline hover:text-accent-mint"
    >
      <Icon icon={Sparkles} size={13} className="mt-0.5 shrink-0 text-accent-mint" aria-hidden />
      <span className="min-w-0">
        <b className="block truncate font-bold">{summary.title}</b>
        <span className="block text-ink-panel-muted">{summary.detail}</span>
      </span>
    </Link>
  );
}

/** Compact plan label for the mobile top bar. */
export function PlanStatusPill() {
  const { data: session } = authClient.useSession();
  const access = useAccess(Boolean(session));
  if (!access.data) return null;
  const summary = summariseAccess(access.data);

  return (
    <Link
      to={summary.tone === 'active' ? '/account/billing' : '/pricing'}
      className="focus-ring inline-flex h-7 max-w-[9rem] items-center truncate rounded-pill bg-primary-tint px-2.5 text-[13px] font-bold text-primary-hover no-underline"
      title={`${summary.title}. ${summary.detail}`}
    >
      {summary.title}
    </Link>
  );
}

/** Desktop top bar: the same status as the old sidebar card, on one line at the right. */
export function PlanStatusBar() {
  const { data: session } = authClient.useSession();
  const access = useAccess(Boolean(session));
  if (access.isPending) return <Skeleton className="h-9 w-64" />;
  if (access.isError) return null;
  const summary = summariseAccess(access.data);
  const to = summary.tone === 'active' ? '/account/billing' : '/pricing';

  return (
    <Link
      to={to}
      className="focus-ring flex max-w-md items-center gap-2 rounded-pill bg-ink-panel px-4 py-2 text-[13px] leading-tight text-ink-panel-text no-underline hover:text-accent-mint"
      title={`${summary.title}. ${summary.detail}`}
    >
      <Icon icon={Sparkles} size={14} className="shrink-0 text-accent-mint" aria-hidden />
      <b className="shrink-0 font-bold">{summary.title}</b>
      <span className="hidden min-w-0 truncate text-ink-panel-muted xl:inline">
        {summary.detail}
      </span>
    </Link>
  );
}
